export type PartnerUser = {permissions?: unknown} | null | undefined;

export function hasPartnerPermission(user: PartnerUser, permission: string): boolean {
  if (!user) return false;
  if (!Object.prototype.hasOwnProperty.call(user, 'permissions')) return true;
  return Array.isArray(user.permissions) && user.permissions.includes(permission);
}

export function hasExplicitPartnerPermission(user: PartnerUser, permission: string): boolean {
  return !!user && Array.isArray(user.permissions) && user.permissions.includes(permission);
}

/**
 * How the client applies a route's permission.
 *
 * - `shadow`: a legacy page. The backend only logs a missing grant and never
 *   blocks (B2), so the client lets every authenticated user in — even with
 *   `permissions: []` (Autocare staff, company_role_id 0) or a narrower
 *   operator list. Blocking here would lock real users out of pages the
 *   server still serves them.
 * - `enforced`: a new page whose endpoints enforce the grant; the client
 *   mirrors that with `hasPartnerPermission`.
 */
export type PartnerRoutePermissionMode = 'shadow' | 'enforced';

type RoutePermissionRule = {pattern: RegExp; permission: string | null; mode: PartnerRoutePermissionMode | null};

// First match wins: keep specific enforced sub-routes above their legacy
// prefix (e.g. X13 message templates under `/settings`).
const routePermissions: RoutePermissionRule[] = [
  // Ana has only the company feature gate; all company users can enter.
  {pattern: /^\/ana(?:\/|$)/, permission: null, mode: null},
  {pattern: /^\/bookings(?:\/|$)/, permission: 'bookings.read', mode: 'shadow'},
  {pattern: /^\/departures(?:\/|$)/, permission: 'routes.read', mode: 'shadow'},
  {pattern: /^\/routes\/?$/, permission: 'routes.read', mode: 'shadow'},
  {pattern: /^\/routes\/(?:create|[^/]+)$/, permission: 'routes.write', mode: 'shadow'},
  {pattern: /^\/(?:buses|bus-templates|properties|price-types)\/(?:create|new|[^/]+)$/, permission: 'routes.write', mode: 'shadow'},
  {pattern: /^\/(?:buses|bus-templates|properties|price-types)\/?$/, permission: 'routes.read', mode: 'shadow'},
  {pattern: /^\/users(?:\/|$)/, permission: 'staff.manage', mode: 'shadow'},
  // X13 — new page; its API enforces `can-partner:settings.write`.
  {pattern: /^\/settings\/message-templates$/, permission: 'settings.write', mode: 'enforced'},
  {pattern: /^\/(?:settings|platforms|booking-sources|email-accounts)(?:\/|$)/, permission: 'settings.write', mode: 'shadow'},
  {pattern: /^\/analytics(?:\/|$)/, permission: 'reports.read', mode: 'enforced'},
  {pattern: /^\/settlements(?:\/|$)/, permission: 'settlements.read', mode: 'enforced'},
];

// Tolerates a missing path (partial route objects in guard callers/tests).
const normalizePartnerPath = (path: string | null | undefined): string => {
  const value = typeof path === 'string' ? path : '';
  return value.length > 1 ? value.replace(/\/+$/, '') : value;
};

function routePermissionRule(path: string): RoutePermissionRule | undefined {
  const normalizedPath = normalizePartnerPath(path);
  return routePermissions.find(({pattern}) => pattern.test(normalizedPath));
}

export function permissionForPartnerRoute(path: string): string | null {
  return routePermissionRule(path)?.permission ?? null;
}

export function permissionModeForPartnerRoute(path: string): PartnerRoutePermissionMode | null {
  return routePermissionRule(path)?.mode ?? null;
}

/**
 * Legacy (shadow) features: the backend logs but never blocks, so any
 * authenticated user keeps them regardless of their permission list.
 */
export function hasShadowPartnerPermission(user: PartnerUser, _permission: string): boolean {
  return !!user;
}

/**
 * Route-level permission decision used by the router guard and the aside
 * menu: shadow routes are open to any user object, enforced routes need the
 * grant (`hasPartnerPermission`), unmapped routes are not permission-gated.
 */
export function canEnterPartnerRoute(user: PartnerUser, path: string): boolean {
  const rule = routePermissionRule(path);
  if (!rule || rule.permission === null) return true;
  if (rule.mode === 'shadow') return hasShadowPartnerPermission(user, rule.permission);
  return hasPartnerPermission(user, rule.permission);
}

export function hasAllExplicitPartnerPermissions(user: PartnerUser, permissions: readonly string[]): boolean {
  return permissions.every(permission => hasExplicitPartnerPermission(user, permission));
}

// All-of explicit grants (no legacy fallback). Create matches before the
// general promotions pattern: the editor's catalogs need `routes.read` and
// saving needs `pricing.write`, so a write-only actor cannot enter it.
const explicitRoutePermissions: Array<[RegExp, string[]]> = [
  [/^\/promotions\/create$/, ['routes.read', 'pricing.write']],
  [/^\/promotions(?:\/[^/]+)?$/, ['routes.read']],
];

export function explicitPermissionsForPartnerRoute(path: string): string[] {
  const normalizedPath = normalizePartnerPath(path);
  return [...(explicitRoutePermissions.find(([pattern]) => pattern.test(normalizedPath))?.[1] ?? [])];
}

export const canReadPromotions = (user: PartnerUser): boolean => hasExplicitPartnerPermission(user, 'routes.read');
export const canWritePromotions = (user: PartnerUser): boolean => hasExplicitPartnerPermission(user, 'pricing.write');
export const canCreatePromotions = (user: PartnerUser): boolean =>
  hasAllExplicitPartnerPermissions(user, ['routes.read', 'pricing.write']);
